import { useState, useCallback } from 'react';
import { openPrintWindow } from '@/lib/print-document';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSocketEvent } from '@/hooks/useSocketEvent';
import { useNavigate, Link } from 'react-router-dom';
import { BookOpen, ChevronRight, AlertTriangle, Printer, Download, MessageSquare, Info } from 'lucide-react';
import toast from 'react-hot-toast';
import { Button } from '@/components/ui/button';
import { useAuthStore } from '@/stores/auth.store';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader } from '@/components/ui/card';

// POS-I147: every amount on this page always shows two decimals (Rs 8,346.80, Rs 3,850.00).
const moneyFmt = new Intl.NumberFormat('en-PK', { style: 'currency', currency: 'PKR', minimumFractionDigits: 2, maximumFractionDigits: 2 });
const money = (n: number) => moneyFmt.format(Number.isFinite(Number(n)) ? Number(n) : 0);
import api from '@/lib/api';
import { isPakistaniMobile } from '@/lib/utils';
import { PageHeader } from '@/components/ui/page-header';
import { SkeletonTable } from '@/components/ui/skeleton-table';
import { EmptyState } from '@/components/ui/empty-state';
import { PaginationBar } from '@/components/ui/pagination-bar';
import { Table, THead, TBody, Tr, Th, Td } from '@/components/ui/table';

interface KhataCustomerRow {
  id: string;
  name: string;
  phone?: string | null;
  creditLimit?: number | null;
  outstandingBalance: number;
  lastActivityAt?: string | null;
}

type ApiErr = { response?: { data?: { message?: string } } };
const esc = (v: string) => v.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
const csvCell = (v: string | number | null | undefined) => {
  const x = String(v ?? '');
  return /[",\n]/.test(x) ? '"' + x.replace(/"/g, '""') + '"' : x;
};

export function KhataPage() {
  const { t } = useTranslation('customers');
  const navigate = useNavigate();
  const { hasPermission } = useAuthStore();
  const canCollect = hasPermission('customers:write') || hasPermission('customers:*');
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  // POS-I143: sortable columns (server side, so the order holds across pages).
  type SortKey = 'name' | 'creditLimit' | 'outstanding' | 'lastActivity';
  const [sort, setSort] = useState<SortKey>('outstanding');
  const [dir, setDir] = useState<'asc' | 'desc'>('desc');
  const toggleSort = (key: SortKey) => {
    if (sort === key) setDir(d => (d === 'asc' ? 'desc' : 'asc'));
    else { setSort(key); setDir(key === 'name' ? 'asc' : 'desc'); }
    setPage(1);
  };
  const sortMark = (key: SortKey) => (sort === key ? (dir === 'asc' ? ' ▲' : ' ▼') : '');
  const daysSince = (iso?: string | null) => (iso ? Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 86400000)) : null);

  const queryClient = useQueryClient();
  const handleKhataUpdate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['khata-outstanding'] });
  }, [queryClient]);
  useSocketEvent('sale.created', handleKhataUpdate);
  useSocketEvent('sale.voided', handleKhataUpdate);
  useSocketEvent('customer.updated', handleKhataUpdate);

  // POS-I163: a failed load must show an error with Retry (never an empty list and Rs 0.00); one automatic retry after a short backoff.
  const { data, isLoading, isError, refetch, isFetching } = useQuery({
    queryKey: ['khata-outstanding', page, search, sort, dir],
    queryFn: () => api.get('/customers/khata/outstanding', { params: { page, limit: 20, search: search || undefined, sort, dir } }).then(r => r.data),
    retry: 1,
    retryDelay: 1500,
  });

  // POS-I144: SMS payment reminders through the Communications module.
  const { data: reminderStatus } = useQuery<{ smsEnabled: boolean }>({
    queryKey: ['khata-reminder-status'],
    queryFn: () => api.get('/customers/khata/reminders/status').then(r => r.data),
    enabled: canCollect,
    retry: false,
  });
  const smsEnabled = reminderStatus?.smsEnabled ?? true; // unknown yet: no banner
  const [reminderState, setReminderState] = useState<Record<string, 'sending' | 'sent' | 'not_sent'>>({});
  const [remindingAll, setRemindingAll] = useState(false);
  const remindOne = async (c: KhataCustomerRow) => {
    setReminderState(s => ({ ...s, [c.id]: 'sending' }));
    try {
      const { data: r } = await api.post(`/customers/${c.id}/ledger/remind`);
      if (r.outcome === 'sent') {
        toast.success(t('khToastSent', { name: c.name }));
        setReminderState(s => ({ ...s, [c.id]: 'sent' }));
      } else if (r.outcome === 'not_configured') {
        toast.error(t('khToastNoProvider'));
        setReminderState(s => ({ ...s, [c.id]: 'not_sent' }));
        queryClient.invalidateQueries({ queryKey: ['khata-reminder-status'] });
      } else {
        toast.error(r.message || t('khToastNotSent'));
        setReminderState(s => ({ ...s, [c.id]: 'not_sent' }));
      }
    } catch (e) {
      toast.error((e as ApiErr)?.response?.data?.message || t('khToastSendFail'));
      setReminderState(s => { const n = { ...s }; delete n[c.id]; return n; });
    }
  };
  const remindAllOverdue = async () => {
    if (!window.confirm(t('khConfirmAll'))) return;
    setRemindingAll(true);
    try {
      const { data: r } = await api.post('/customers/khata/reminders/overdue');
      if (!r.smsEnabled) toast.error(t('khToastNoProviderAll'));
      else if (r.total === 0) toast(t('khToastNoOverdue'));
      else toast.success(`${t('khToastAllResult', { sent: r.sent, total: r.total })}${r.skipped ? t('khToastSkipped', { count: r.skipped }) : ''}${r.failed ? t('khToastFailed', { count: r.failed }) : ''}`);
      queryClient.invalidateQueries({ queryKey: ['khata-reminder-status'] });
    } catch (e) {
      toast.error((e as ApiErr)?.response?.data?.message || t('khToastSendAllFail'));
    } finally {
      setRemindingAll(false);
    }
  };

  const [exporting, setExporting] = useState(false);
  /** POS-I145: the whole filtered, sorted debtors list (not just this page) as CSV or a print view. */
  const exportList = async (mode: 'csv' | 'print') => {
    setExporting(true);
    try {
      const res = await api.get('/customers/khata/outstanding', { params: { page: 1, limit: 5000, search: search || undefined, sort, dir } });
      const rows: KhataCustomerRow[] = res.data?.data ?? [];
      const sum = rows.reduce((a, r) => a + Number(r.outstandingBalance), 0);
      const today = new Date().toISOString().slice(0, 10);
      const last = (r: KhataCustomerRow) => (r.lastActivityAt ? new Date(r.lastActivityAt).toLocaleDateString() : '');
      if (mode === 'csv') {
        const lines = [
          ['Customer', 'Phone', 'Credit limit', 'Outstanding', 'Last activity'].map(csvCell).join(','),
          ...rows.map(r => [r.name, r.phone ?? '', r.creditLimit ?? '', r.outstandingBalance, last(r)].map(csvCell).join(',')),
          ['Total', '', '', sum, ''].map(csvCell).join(','),
        ];
        const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = `khata-debtors-${today}.csv`;
        a.click();
        URL.revokeObjectURL(a.href);
        return;
      }
      const body = rows.map(r => `<tr><td>${esc(r.name)}</td><td>${esc(r.phone ?? '')}</td><td class="r">${r.creditLimit ? esc(money(r.creditLimit)) : ''}</td><td class="r">${esc(money(r.outstandingBalance))}</td><td>${esc(last(r))}</td></tr>`).join('');
      // POS-I240: shared printable-document builder (Powered by footer on every page).
      if (!openPrintWindow({ title: 'Khata / Udhar', bodyHtml: `<h1>Khata / Udhar: customers who owe</h1><div class="sub">Printed ${esc(new Date().toLocaleDateString())}</div><table><thead><tr><th>Customer</th><th>Phone</th><th class="r">Credit limit</th><th class="r">Outstanding</th><th>Last activity</th></tr></thead><tbody>${body}<tr class="tot"><td colspan="3">Total</td><td class="r">${esc(money(sum))}</td><td></td></tr></tbody></table>` })) { toast.error(t('khToastPopup')); return; }
    } catch {
      toast.error(t('khToastExportFail'));
    } finally {
      setExporting(false);
    }
  };

  const entries: KhataCustomerRow[] = data?.data ?? [];
  const total = data?.total ?? 0;
  const totalOutstanding = data?.totalOutstanding ?? 0;
  const walkInBalance: number = data?.walkInBalance ?? 0;
  const pendingChequesTotal: number = data?.pendingChequesTotal ?? 0;
  const receivablesTotal = totalOutstanding + walkInBalance + pendingChequesTotal;

  // POS-I146: tie the khata total to account 1100 Accounts Receivable in the books (Trial Balance).
  const { data: trialBalance, isLoading: tbLoading, isError: tbError, refetch: refetchTb } = useQuery<{ accounts: { accountCode: string; accountName: string; balance: number }[] }>({
    queryKey: ['trial-balance'],
    queryFn: () => api.get('/reports/trial-balance').then(r => r.data),
    enabled: hasPermission('accounting:read') || hasPermission('accounting:*'),
    retry: 1,
    retryDelay: 1500,
  });
  const listFailed = isError;
  const totalsReady = !isLoading && !isError && !!data;
  const glReceivable = trialBalance?.accounts?.find(a => a.accountCode === '1100');
  const glDifference = totalsReady && glReceivable ? Math.round((receivablesTotal - Number(glReceivable.balance)) * 100) / 100 : null;

  return (
    <div className="space-y-6">
      <PageHeader title={t('khPageTitle')} />

      {/* Summary */}
      <Card className="border-warning/30 bg-warning/10">
        <CardContent className="pt-4">
          <div className="flex items-center gap-3">
            <AlertTriangle className="h-5 w-5 text-warning-text" />
            <div>
              <p className="text-sm text-muted-foreground">{t('khataTotal')}</p>
              {totalsReady ? (
                <p className="text-2xl font-bold text-warning-text">{money(totalOutstanding)}</p>
              ) : listFailed ? (
                <p className="text-2xl font-bold text-muted-foreground" data-testid="khata-total-unavailable">—</p>
              ) : (
                <div className="mt-1 h-8 w-40 animate-pulse rounded bg-muted" data-testid="khata-total-loading" aria-busy="true" />
              )}
            </div>
          </div>
          {totalsReady && (walkInBalance > 0 || pendingChequesTotal > 0) && (
            <dl className="mt-3 space-y-1 border-t border-warning/30 pt-3 text-sm">
              {walkInBalance > 0 && (
                <div className="flex justify-between">
                  <dt className="text-muted-foreground">{t('walkInUnassigned')}</dt>
                  <dd className="font-medium">{money(walkInBalance)}</dd>
                </div>
              )}
              {pendingChequesTotal > 0 && (
                <div className="flex justify-between">
                  <dt className="text-muted-foreground">{t('pendingCheques')}</dt>
                  <dd className="font-medium">{money(pendingChequesTotal)}</dd>
                </div>
              )}
              <div className="flex justify-between border-t border-warning/30 pt-1 font-semibold">
                <dt>{t('receivablesTotal')}</dt>
                <dd>{money(receivablesTotal)}</dd>
              </div>
            </dl>
          )}
          {totalsReady && tbError && (
            <p className="mt-3 flex items-center gap-2 border-t border-warning/30 pt-3 text-sm text-muted-foreground" role="alert">
              {t('khBooksLoadFail')}
              <Button size="sm" variant="outline" onClick={() => void refetchTb()}>{t('khRetry')}</Button>
            </p>
          )}
          {totalsReady && tbLoading && (
            <div className="mt-3 h-16 animate-pulse rounded bg-muted" aria-busy="true" />
          )}
          {glReceivable && glDifference !== null && (
            <dl className="mt-3 space-y-1 border-t border-warning/30 pt-3 text-sm" aria-label={t('khBalancesAria')}>
              <div className="flex justify-between">
                <dt className="text-muted-foreground">{t('khCustomerBalances')}</dt>
                <dd className="font-medium">{money(totalOutstanding)}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-muted-foreground">{t('khPendingChequesNotCleared')}</dt>
                <dd className="font-medium">{money(pendingChequesTotal)}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-muted-foreground">{t('khAccount1100')}</dt>
                <dd className="font-medium">{money(Number(glReceivable.balance))}</dd>
              </div>
              <div className="flex justify-between border-t border-warning/30 pt-1 font-semibold">
                <dt>{t('khDifference')}</dt>
                <dd className={Math.abs(glDifference) < 0.01 ? 'text-success-text' : 'text-destructive-text'}>
                  {Math.abs(glDifference) < 0.01 ? t('khMatches') : money(glDifference)}
                </dd>
              </div>
              {Math.abs(glDifference) >= 0.01 && (
                <p className="text-xs text-muted-foreground">
                  {t('khGapNote')}
                </p>
              )}
              <Link to="/finance/accounting?tab=trial-balance" className="inline-block text-xs underline">
                {t('khOpenTb')}
              </Link>
            </dl>
          )}
        </CardContent>
      </Card>

      {canCollect && !smsEnabled && (
        <div className="flex items-start gap-2 rounded-md border border-warning/30 bg-warning/10 p-3 text-sm" role="status">
          <Info className="mt-0.5 h-4 w-4 text-warning-text" />
          <span>
            {t('khSmsOff')}{' '}
            <Link to="/admin/settings" className="underline">{t('khSettings')}</Link>.
          </span>
        </div>
      )}

      {/* Customer list */}
      <Card>
        <CardHeader>
          <Input
            placeholder={t('khSearchPlaceholder')}
            value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }}
            className="max-w-xs"
          />
          <div className="flex gap-2">
            {canCollect && (
              <Button variant="outline" size="sm" onClick={() => void remindAllOverdue()} disabled={remindingAll || entries.length === 0}>
                <MessageSquare className="h-4 w-4 me-2" /> {t('khRemindAll')}
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={() => void exportList('print')} disabled={exporting || entries.length === 0}><Printer className="h-4 w-4 me-2" /> {t('khPrintList')}</Button>
            <Button variant="outline" size="sm" onClick={() => void exportList('csv')} disabled={exporting || entries.length === 0}><Download className="h-4 w-4 me-2" /> {t('khExportCsv')}</Button>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? <SkeletonTable rows={8} cols={4} /> : listFailed ? (
            <div className="flex flex-col items-center gap-3 py-10 text-center" role="alert" data-testid="khata-load-error">
              <AlertTriangle className="h-8 w-8 text-destructive-text" />
              <p className="font-medium">{t('khLoadFailTitle')}</p>
              <p className="text-sm text-muted-foreground">{t('khLoadFailDesc')}</p>
              <Button variant="outline" size="sm" onClick={() => void refetch()} disabled={isFetching}>{t('khRetry')}</Button>
            </div>
          ) : entries.length === 0 ? (
            search.trim() ? (
              <EmptyState icon={BookOpen} title={t('khEmptySearchTitle')} description={t('khEmptySearchDesc')} />
            ) : (
              <EmptyState icon={BookOpen} title={t('khEmptyTitle')} description={t('khEmptyDesc')} />
            )
          ) : (
            <Table mobileCards>
              <THead>
                <Tr hover={false}>
                  <Th><button type="button" className="font-semibold" onClick={() => toggleSort('name')}>{t('colCustomer')}{sortMark('name')}</button></Th>
                  <Th>{t('phone')}</Th>
                  <Th className="text-end"><button type="button" className="font-semibold" onClick={() => toggleSort('creditLimit')}>{t('creditLimit')}{sortMark('creditLimit')}</button></Th>
                  <Th className="text-end"><button type="button" className="font-semibold" onClick={() => toggleSort('outstanding')}>{t('colOutstanding')}{sortMark('outstanding')}</button></Th>
                  <Th className="text-end"><button type="button" className="font-semibold" onClick={() => toggleSort('lastActivity')}>{t('colLastActivity')}{sortMark('lastActivity')}</button></Th>
                  {canCollect && <Th className="text-end">{t('colAction')}</Th>}
                </Tr>
              </THead>
              <TBody>
                {entries.map((c) => (
                  <Tr
                    key={c.id}
                    className="cursor-pointer"
                    onClick={() => navigate(`/customers/${c.id}/ledger`)}
                  >
                    <Td label={t('colCustomer')} className="td-name font-medium">{c.name}</Td>
                    <Td label={t('phone')} className="text-muted-foreground text-xs">{c.phone ?? '—'}</Td>
                    <Td label={t('creditLimit')} className="text-end text-muted-foreground">{c.creditLimit ? money(c.creditLimit) : '—'}</Td>
                    <Td label={t('colOutstanding')} className="text-end">
                      <span className={`font-bold ${c.outstandingBalance > 0 ? 'text-destructive-text' : 'text-success-text'}`}>
                        {money(Math.abs(c.outstandingBalance))}
                      </span>
                    </Td>
                    <Td label={t('colLastActivity')} className="text-end text-xs text-muted-foreground">
                      {c.lastActivityAt ? new Date(c.lastActivityAt).toLocaleDateString() : '—'}
                      {daysSince(c.lastActivityAt) !== null && (
                        <span className={`ms-1 ${(daysSince(c.lastActivityAt) ?? 0) >= 60 ? 'text-destructive-text font-medium' : (daysSince(c.lastActivityAt) ?? 0) >= 30 ? 'text-warning-text' : ''}`}>({daysSince(c.lastActivityAt)}d)</span>
                      )}
                      <ChevronRight className="inline h-4 w-4 ms-1" />
                    </Td>
                    {canCollect && (
                      <Td label={t('colAction')} className="text-end">
                        <div className="flex items-center justify-end gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            disabled={!isPakistaniMobile(c.phone) || reminderState[c.id] === 'sending'}
                            title={isPakistaniMobile(c.phone) ? t('khRemindTip') : t('khNoValidPhone')}
                            onClick={(e) => { e.stopPropagation(); void remindOne(c); }}
                          >
                            <MessageSquare className="h-4 w-4 me-1" /> {reminderState[c.id] === 'sending' ? t('khSending') : t('khRemind')}
                          </Button>
                          {reminderState[c.id] === 'sent' && <span className="text-xs text-success-text">{t('khSent')}</span>}
                          {reminderState[c.id] === 'not_sent' && <span className="text-xs text-warning-text">{t('khNotSent')}</span>}
                          <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); navigate(`/customers/${c.id}/ledger?pay=1`); }}>
                            {t('khCollect')}
                          </Button>
                        </div>
                      </Td>
                    )}
                  </Tr>
                ))}
              </TBody>
            </Table>
          )}
          {!listFailed && <PaginationBar page={page} pageSize={20} total={total} onPageChange={setPage} />}
        </CardContent>
      </Card>
    </div>
  );
}

